import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { resolveImg } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const [addr, setAddr] = useState({ name: "", street: "", city: "", state: "", zip: "", country: "US" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const total = subtotal;

  const handleCheckout = async () => {
    if (items.length === 0) return;
    if (!addr.name || !addr.street || !addr.city || !addr.zip) {
      toast.error("Please fill your shipping address");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_address: addr,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container-app px-6 py-20 text-center">
        <ShoppingBag size={56} className="mx-auto text-[#638971] mb-4"/>
        <h1 className="font-heading text-3xl font-bold text-[#1A1A1A]">Your cart is empty</h1>
        <p className="text-[#595959] mt-2">Start shopping to fill it with essentials.</p>
        <Link to="/products" data-testid="empty-cart-shop-btn">
          <Button className="mt-6 rounded-full bg-[#638971] hover:bg-[#4F705B] text-white px-7">Browse Products</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-app px-6 md:px-10 py-10">
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-8">Your cart</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-10">
        <div className="space-y-4">
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`}
                 className="flex gap-4 bg-white rounded-xl border border-gray-100 p-4">
              <img src={resolveImg(i.image_url)} alt={i.name} className="w-24 h-24 object-cover rounded-lg"/>
              <div className="flex-1">
                <div className="font-heading font-semibold text-[#1A1A1A]">{i.name}</div>
                <div className="text-sm text-[#638971] mt-1">${i.price.toFixed(2)}</div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="inline-flex items-center rounded-full border border-gray-200">
                    <button data-testid={`cart-qty-minus-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity - 1)}
                            className="w-8 h-8 grid place-items-center rounded-l-full hover:bg-gray-50">
                      <Minus size={12}/>
                    </button>
                    <div className="px-3 text-sm font-semibold">{i.quantity}</div>
                    <button data-testid={`cart-qty-plus-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 1)}
                            className="w-8 h-8 grid place-items-center rounded-r-full hover:bg-gray-50">
                      <Plus size={12}/>
                    </button>
                  </div>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.product_id)}
                          className="text-[#595959] hover:text-red-600 flex items-center gap-1 text-sm">
                    <Trash2 size={14}/> Remove
                  </button>
                </div>
              </div>
              <div className="font-heading font-bold text-[#2C4035]">${(i.price * i.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <aside className="bg-white rounded-2xl border border-gray-100 p-6 h-fit sticky top-24">
          <h2 className="font-heading text-xl font-bold text-[#1A1A1A]">Order summary</h2>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#595959]">Subtotal</span><span data-testid="cart-subtotal" className="font-semibold">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs">
              <span className="text-[#595959]">Shipping</span>
              <span className="text-[#638971] font-medium">Calculated at checkout</span>
            </div>
            <div className="border-t border-gray-100 pt-3 flex justify-between text-base">
              <span className="font-semibold text-[#1A1A1A]">Subtotal</span>
              <span data-testid="cart-total" className="font-heading font-bold text-[#2C4035]">${total.toFixed(2)}</span>
            </div>
            <div className="text-[11px] text-[#595959] leading-relaxed">
              Choose your shipping speed on the next screen — rates depend on your address (US domestic, priority, or international).
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-semibold text-[#1A1A1A]">Shipping address</h3>
            <div className="grid grid-cols-1 gap-2">
              <Input data-testid="ship-name" placeholder="Full name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })}/>
              <Input data-testid="ship-street" placeholder="Street address" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })}/>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="ship-city" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })}/>
                <Input data-testid="ship-state" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })}/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="ship-zip" placeholder="ZIP" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })}/>
                <Input data-testid="ship-country" placeholder="Country" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })}/>
              </div>
            </div>
          </div>

          <Button data-testid="checkout-btn" onClick={handleCheckout} disabled={loading}
                  className="mt-6 w-full rounded-full bg-[#638971] hover:bg-[#4F705B] text-white h-12 text-base">
            {loading ? "Redirecting…" : "Checkout with Stripe"}
          </Button>
          <p className="text-[11px] text-[#595959] text-center mt-3">Test card: 4242 4242 4242 4242 · any future date · any CVC</p>
        </aside>
      </div>
    </div>
  );
}
